import {
  App,
  Divider,
  Form,
  InputNumber,
  Modal,
  Table,
  Typography,
} from 'antd';
import { ColumnType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { MdOutlineRemoveCircle } from 'react-icons/md';
import { TResponse } from '../../../../../common';
import { axiosApi } from '../../../../../core';
import {
  SearchOption,
  SearchSelect,
} from '../../../../../core/components/SearchSelect';
import {
  useAssignOfferMutation,
  useGetStaffOffersQuery,
  useUnassignOfferMutation,
} from '../../../../staff/store/staff.api';
import {
  AssignedOfferResponse,
  AssignFormAttributes,
  AssignOffersProps,
  SpecialOfferOwnerEnum,
} from '../../../../staff/types';
import { IOffer } from '../types';

const { Link } = Typography;

export interface OfferOption {
  label: string;
  value: string;
  quantity: number;
}

export const AssignOffers: React.FC<AssignOffersProps> = ({
  title,
  open,
  user,
  owner,
  setOpen,
}) => {
  const [maxQuantity, setMaxQuantity] = useState<number>(0);
  const [form] = Form.useForm();
  const [assignOffer, { error: assigningError }] = useAssignOfferMutation();
  const [unassignOffer] = useUnassignOfferMutation();
  const { data: assignedOffers, refetch } = useGetStaffOffersQuery({
    id: user.id,
  });
  const { message } = App.useApp();

  const columns = [
    {
      title: 'Merchant',
      dataIndex: 'merchant',
      key: 'merchant',
    },
    {
      title: 'Offer',
      dataIndex: 'offer',
      key: 'offer',
    },
    owner === SpecialOfferOwnerEnum.STAFF && {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Action',
      width: 80,
      render: (record: any) => (
        <Link onClick={() => onUnassignOffer(record.id)}>
          <MdOutlineRemoveCircle size={20} />
        </Link>
      ),
      key: 'action',
    },
  ].filter(Boolean) as Array<ColumnType<AssignedOfferResponse>>;

  const searchUsers = useCallback(
    async (search: string): Promise<SearchOption[]> => {
      try {
        const response = await axiosApi.get<TResponse<IOffer[]>>(
          `/offers?search=${search}&filter[is_special]=true`,
        );

        const { data } = response.data;

        return data.map((offer) => {
          const label =
            owner === SpecialOfferOwnerEnum.STAFF
              ? `R${offer.attributes.remaining_special_quantity} | ${offer.attributes.merchant.name} | ${offer.attributes.name}`
              : `${offer.attributes.merchant.name} | ${offer.attributes.name}`;
          return {
            label,
            value: offer.id,
            quantity: offer.attributes.remaining_special_quantity,
          };
        });
      } catch (error) {
        console.error('Failed to fetch offers:', error);
        return [];
      }
    },
    [owner],
  );

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onFinish = useCallback(
    (data: AssignFormAttributes) => {
      assignOffer({
        id: user.id,
        offer_id: data.offer_id,
        quantity: owner === SpecialOfferOwnerEnum.STAFF ? data.quantity : 1,
        owner: owner,
      });
    },
    [assignOffer, owner, user.id],
  );

  const onUnassignOffer = useCallback(
    (offerId: string) => {
      unassignOffer({ id: user.id, offer_id: offerId });
    },
    [unassignOffer, user.id],
  );

  const onOfferChange = useCallback((offerId: any, offer: any) => {
    setMaxQuantity((offer as OfferOption).quantity);
  }, []);

  useEffect(() => {
    if (!assigningError) {
      return;
    }

    const { data } = assigningError as { data: { message: string } };

    message.error(data.message);
  }, [assigningError, message]);

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width={720}
      okText={owner === SpecialOfferOwnerEnum.STAFF ? 'Assign' : 'Send'}
      onOk={() => form.submit()}
    >
      <Form form={form} layout="horizontal" onFinish={onFinish}>
        <Form.Item label="Offers" name="offer_id" rules={[{ required: true }]}>
          <SearchSelect
            fetchOptions={searchUsers}
            onChange={onOfferChange}
            placeholder="Searching offers..."
          />
        </Form.Item>
        {owner !== SpecialOfferOwnerEnum.CUSTOMER && (
          <Form.Item
            label="Quantity"
            name="quantity"
            rules={[
              { required: true, message: 'Required' },
              {
                validator: (_, value) => {
                  if (value && value > maxQuantity) {
                    return Promise.reject(
                      `Value must be less than or equal to ${maxQuantity}`,
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber min={1} />
          </Form.Item>
        )}
      </Form>
      <Divider />
      <Table columns={columns} dataSource={assignedOffers} />
    </Modal>
  );
};
